import { logger } from "../utils/logging";
import { findBrandByDomain } from "./configLoader";
import CUSTOM_FONT_CONFIG, { FontVariant, FontConfig } from "./customFonts";

const CUSTOM_FONTS = [
  "Futura",
  "Graphik",
  "Arial",
  "Times New Roman",
  "Optima",
  "HafferSQ",
  "Vectora LT Std",
  "Dinot",
  "Brown",
  "Gilmer",
  "AktivGrotesk",
  "FuturaProLight",
  "MillerBanner",
  "Akuratt",
  "Gravity",
  "Metropolis",
  "Helvetica",
  "apercu",
  "BashTodo",
  "Baton Turbo",
];

async function loadFontVariant(
  family: string,
  variant: FontVariant,
): Promise<FontFace> {
  const {
    url,
    weight = 400,
    style = "normal",
    format = "truetype",
    local,
  } = variant;

  const srcLocal = local ? `local("${local}"), ` : "";
  const src = `${srcLocal}url("${url}") format("${format}")`;

  const font = new FontFace(family, src, {
    weight: String(weight),
    style,
  });

  document.fonts.add(font);
  await font.load();

  return font;
}

async function loadFontConfig(fontConfig: FontConfig) {
  await Promise.all(
    fontConfig.variants.map((v) => loadFontVariant(fontConfig.family, v)),
  );
}

export async function loadCustomFontByName(name: string) {
  const fontConfig = CUSTOM_FONT_CONFIG.find(
    (f) => f.family.toLowerCase() === name.toLowerCase(),
  );

  if (!fontConfig) {
    throw new Error(`Custom font config not found for ${name}`);
  }

  await loadFontConfig(fontConfig);
}

export const loadGoogleFont = (fontStr: string): Promise<void> => {
  const { name, italic } = parseFontString(fontStr);

  if (CUSTOM_FONTS.includes(name)) {
    logger.log(
      `The font ${name} is a custom font and doesn't require loading from Google Fonts.`,
    );
    return Promise.resolve();
  }

  const italicParam = italic ? "1" : "0";

  const fontUrl = `https://fonts.googleapis.com/css2?family=${name.replace(
    / /g,
    "+",
  )}:ital,wght@${italicParam},100;${italicParam},200;${italicParam},300;${italicParam},400;${italicParam},500;${italicParam},600;${italicParam},700;${italicParam},800;${italicParam},900&display=swap`;

  return new Promise((resolve, reject) => {
    const link = document.createElement("link");
    link.href = fontUrl;
    link.rel = "stylesheet";
    link.onload = () => resolve();
    link.onerror = () => reject(new Error(`Failed to load font: ${name}`));
    document.head.appendChild(link);
  });
};

const parseFontString = (fontStr: string) => {
  const parts = fontStr.split(",").map((part) => part.trim());

  const italic = parts.some((p) => p.toLowerCase() === "italic");
  const bold = parts.some((p) => p.toLowerCase() === "bold");

  const name = parts
    .filter((p) => p.toLowerCase() !== "italic" && p.toLowerCase() !== "bold")
    .join(", ")
    .trim();

  return { name, italic, bold };
};

export const extractFontFamily = (url: string) => {
  const match = url.match(/family=([^:&,]+)/);
  if (match) {
    return match[1].replace(/\+/g, " ");
  }
  return url;
};

const STYLE_ID = "app-font-style";

const createOrUpdateStyle = (fontFamily: string) => {
  let style = document.getElementById(STYLE_ID) as HTMLStyleElement | null;

  if (!style) {
    style = document.createElement("style");
    style.id = STYLE_ID;
    document.head.appendChild(style);
  }

  style.innerHTML = `
    * {
      font-family: "${fontFamily}", sans-serif !important;
    }

    .fa-regular, .far, .fa, [class^="fa-"], [class*=" fa-"], svg text {
      font-family: "${fontFamily}", sans-serif !important;
      font-weight: 400;
    }

    :host, :root {
      --fa-font-solid: normal 900 1em/1 "${fontFamily}", sans-serif !important;
    }

    ::before, ::after {
      font-family: "${fontFamily}", sans-serif !important;
    }
  `;
};

export const applyFontFromConfig = async (fontStr: string) => {
  try {
    const { name, italic } = parseFontString(fontStr);

    const brandDefined = findBrandByDomain();
    const isPXP = brandDefined?.name === "Project X Paris";

    if (isPXP) {
      await loadCustomFontByName("Gravity");
    }

    if (CUSTOM_FONTS.includes(name)) {
      await loadCustomFontByName(name);
    } else {
      await loadGoogleFont(fontStr);
    }

    const fontFamily = CUSTOM_FONTS.includes(name)
      ? name
      : extractFontFamily(fontStr);

    createOrUpdateStyle(fontFamily);

    document.documentElement.style.fontFamily = `${name}, sans-serif`;
    document.body.style.fontFamily = `${name}, sans-serif`;
    document.documentElement.style.fontStyle = italic ? "italic" : "normal";
    document.body.style.fontStyle = italic ? "italic" : "normal";
  } catch (error) {
    logger.error(`Failed to load the font: ${error}`);
  }
};
